// src/components/admin/MarketingManagement.js
import {
  AlertCircleIcon,
  CalendarIcon, ClockIcon,
  DollarSignIcon,
  FilterIcon,
  MailIcon,
  PauseIcon,
  PhoneIcon,
  PlayIcon,
  PlusIcon,
  RefreshCwIcon,
  SendIcon,
  ShoppingCartIcon,
  CircleStopIcon as StopIcon,
  TrashIcon,
  XIcon
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useConfig } from '../../context/ConfigContext';
import axios from '../../utils/axiosHandler';

const MarketingManagement = () => {
  const { config } = useConfig();
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [stats, setStats] = useState({
    totalCarts: 0,
    totalReminders: 0,
    emailsSent: 0,
    smsSent: 0,
    conversionRate: 0
  });
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('30days');
  const [showModal, setShowModal] = useState(false);
  const [campaignData, setCampaignData] = useState({
    name: 'Cart Recovery Campaign',
    frequency: 'weekly',
    maxReminders: 6,
    channels: ['email', 'sms'],
    reminderInterval: 7,
    emailTemplate: '',
    smsTemplate: '',
    customNextRun: ''
  });
  const [errors, setErrors] = useState({});
  const [deleteModal, setDeleteModal] = useState({ show: false, campaignId: null, campaignName: '' });
  const [reminderModal, setReminderModal] = useState({
    show: false,
    cartId: null,
    cart: null,
    emailContent: '',
    smsContent: '',
    previewMode: true,
    activeTab: 'email',
    loading: false
  });

  // Get business name from config with fallback
  const businessName = config?.siteName || config?.company?.name || 'our store';

  // Fetch abandoned carts and campaign data
  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch data in parallel
      const [cartsResponse, campaignsResponse, statsResponse] = await Promise.all([
        axios.get(`/api/admin/marketing/abandoned-carts?timeRange=${timeRange}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('/api/admin/marketing/campaigns', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`/api/admin/marketing/stats?timeRange=${timeRange}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Check for errors
      if (cartsResponse.status !== 200) throw new Error('Failed to fetch abandoned carts');
      if (campaignsResponse.status !== 200) throw new Error('Failed to fetch campaign data');
      if (statsResponse.status !== 200) throw new Error('Failed to fetch marketing stats');

      // Parse responses
      const [cartsData, campaignsData, statsData] = await Promise.all([
        cartsResponse.data,
        campaignsResponse.data,
        statsResponse.data
      ]);

      setAbandonedCarts(cartsData);
      setCampaigns(campaignsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching marketing data:', error);
      showNotification('error', 'Failed to load marketing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show notification helper
  const showNotification = (type, message, duration = 3000) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), duration);
  };


  // Function to generate default email template
  const generateDefaultEmailTemplate = (cart) => {
    if (!cart || !cart.user) return '';

    const itemsList = cart.items.map(item => {
      const productName = item.product?.name || 'Product';
      const quantity = item.quantity || 1;
      return `<li>${productName} x ${quantity}</li>`;
    }).join('');

    const totalValue = cart.totalValue || 0;

    return `
    <h2>Hello ${cart.user.firstName || 'there'},</h2>
    <p>We noticed you have items waiting in your cart at ${businessName}.</p>
    <div class="highlight">
      <p><strong>Your cart contains:</strong></p>
      <ul>
        ${itemsList}
      </ul>
      <p><strong>Total Value: $${totalValue.toFixed(2)}</strong></p>
    </div>
    <p>Ready to complete your purchase? Your items are still available!</p>
    <p>If you have any questions or need assistance, our support team is here to help.</p>
  `;
  };

  // Function to generate default SMS template
  const generateDefaultSmsTemplate = (cart) => {
    if (!cart || !cart.user) return '';

    const totalValue = cart.totalValue || 0;
    return `Hi ${cart.user.firstName || 'there'}! You have items worth $${totalValue.toFixed(2)} waiting in your ${businessName} cart. Complete your purchase now: ${window.location.origin}/cart`;
  };

  // Function to open the reminder editor modal
  const openReminderEditor = (cart) => {
    setReminderModal({
      show: true,
      cartId: cart._id,
      cart: cart,
      emailContent: generateDefaultEmailTemplate(cart),
      smsContent: generateDefaultSmsTemplate(cart),
      previewMode: true,
      activeTab: 'email',
      loading: false
    });
  };

  // Function to send the customized reminder
  const sendCustomReminder = async () => {
    try {
      setReminderModal(prev => ({ ...prev, loading: true }));

      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/admin/marketing/send-reminder/${reminderModal.cartId}`, {
        channels: reminderModal.activeTab === 'both' ? ['email', 'sms'] : [reminderModal.activeTab],
        emailContent: reminderModal.emailContent,
        smsContent: reminderModal.smsContent
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });




      if (response.status !== 200) {
        const errorData = await response.data;
        throw new Error(errorData.error || 'Failed to send reminder');
      }

      const result = await response.data;
      console.log('Reminder result:', result);

      // Update cart in the UI
      setAbandonedCarts(prevCarts =>
        prevCarts.map(cart => {
          if (cart._id === reminderModal.cartId) {
            // Count which types of reminders were sent
            const emailSent = result.results && result.results.some(r => r.type === 'email' && r.status === 'sent');
            const smsSent = result.results && result.results.some(r => r.type === 'sms' && r.status === 'sent');

            return {
              ...cart,
              status: 'reminded',
              emailReminders: (cart.emailReminders || 0) + (emailSent ? 1 : 0),
              smsReminders: (cart.smsReminders || 0) + (smsSent ? 1 : 0),
              lastReminderDate: new Date().getTime()
            };
          }
          return cart;
        })
      );

      // Close modal and show notification
      setReminderModal({ show: false, cartId: null, cart: null, emailContent: '', smsContent: '', previewMode: true, activeTab: 'email', loading: false });
      showNotification('success', 'Reminder sent successfully');

      // Refresh stats after sending reminder
      setTimeout(() => {
        fetchData();
      }, 1000);
    } catch (error) {
      console.error('Error sending reminder:', error);
      showNotification('error', error.message || 'Failed to send reminder. Please try again.');
    } finally {
      setReminderModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle campaign actions (pause, resume, stop)
  const handleCampaignAction = async (campaignId, action) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/admin/marketing/campaigns/${campaignId}/${action}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status !== 200 && response.status !== 201) throw new Error(`Failed to ${action} campaign`);

      const updatedCampaign = await response.data;

      // Update campaigns list
      setCampaigns(prevCampaigns =>
        prevCampaigns.map(campaign =>
          campaign._id === campaignId ? updatedCampaign : campaign
        )
      );

      showNotification('success', `Campaign ${action === 'pause' ? 'paused' : action === 'resume' ? 'resumed' : 'stopped'} successfully`);
    } catch (error) {
      console.error(`Error ${action} campaign:`, error);
      showNotification('error', `Failed to ${action} campaign. Please try again.`);
    }
  };

  // Validate campaign form
  const validateCampaignForm = () => {
    const newErrors = {};

    if (!campaignData.name.trim()) {
      newErrors.name = 'Campaign name is required';
    }

    if (campaignData.frequency === 'custom' && (!campaignData.reminderInterval || campaignData.reminderInterval < 1)) {
      newErrors.reminderInterval = 'Please set a valid reminder interval (at least 1 day)';
    }

    if (campaignData.maxReminders < 1) {
      newErrors.maxReminders = 'Maximum reminders must be at least 1';
    }

    if (campaignData.channels.length === 0) {
      newErrors.channels = 'Please select at least one channel';
    }

    if (campaignData.customNextRun) {
      const nextRunDate = new Date(campaignData.customNextRun);
      if (isNaN(nextRunDate.getTime()) || nextRunDate < new Date()) {
        newErrors.customNextRun = 'Please select a valid future date and time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Create new cart recovery campaign
  const createCampaign = async () => {
    // Validate form first
    if (!validateCampaignForm()) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Prepare campaign data
      const data = {
        name: campaignData.name,
        type: 'cart_recovery',
        frequency: campaignData.frequency,
        maxReminders: campaignData.maxReminders,
        channels: campaignData.channels,
        emailTemplate: campaignData.emailTemplate || null,
        smsTemplate: campaignData.smsTemplate || null,
        reminderInterval: campaignData.frequency === 'custom' ? campaignData.reminderInterval : null,
        customNextRun: campaignData.customNextRun || null
      };

      console.log('Creating campaign with data:', data);

      const response = await axios.post('/api/admin/marketing/campaigns', data, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const responseData = await response.data;

      if (response.status !== 200 && response.status !== 201) {
        console.error('Server error creating campaign:', responseData);
        throw new Error(responseData.error || 'Failed to create campaign');
      }

      // Add new campaign to the list
      setCampaigns(prev => [...prev, responseData]);

      // Reset form and close modal
      resetForm();
      setShowModal(false);

      showNotification('success', 'New cart recovery campaign created successfully');

      // Refresh data to see the impact
      setTimeout(fetchData, 1000);
    } catch (error) {
      console.error('Error creating campaign:', error);
      showNotification('error', error.message || 'Failed to create campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!deleteModal.campaignId) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.delete(`/api/admin/marketing/campaigns/${deleteModal.campaignId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status !== 200) {
        const errorData = await response.data;
        throw new Error(errorData.error || 'Failed to delete campaign');
      }

      // Remove the deleted campaign from state
      setCampaigns(prevCampaigns =>
        prevCampaigns.filter(campaign => campaign._id !== deleteModal.campaignId)
      );

      // Close the modal and show success notification
      setDeleteModal({ show: false, campaignId: null, campaignName: '' });
      showNotification('success', 'Campaign deleted successfully');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      showNotification('error', error.message || 'Failed to delete campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add this function to open the delete confirmation modal
  const confirmDeleteCampaign = (campaign) => {
    setDeleteModal({
      show: true,
      campaignId: campaign._id,
      campaignName: campaign.name || 'Cart Recovery Campaign'
    });
  };

  // Reset campaign form
  const resetForm = () => {
    setCampaignData({
      name: 'Cart Recovery Campaign',
      frequency: 'weekly',
      maxReminders: 6,
      channels: ['email', 'sms'],
      reminderInterval: 7,
      emailTemplate: '',
      smsTemplate: '',
      customNextRun: ''
    });
    setErrors({});
  };

  // Filter abandoned carts by status
  const filteredCarts = selectedFilter === 'all'
    ? abandonedCarts
    : abandonedCarts.filter(cart => cart.status === selectedFilter);

  // Send a manual reminder to specific cart
  const sendManualReminder = async (cartId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/admin/marketing/send-reminder/${cartId}`, {
        channels: ['email', 'sms']
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status !== 200) throw new Error('Failed to send reminder');

      const result = await response.data;
      console.log('Reminder result:', result);

      // Update cart in the UI
      setAbandonedCarts(prevCarts =>
        prevCarts.map(cart => {
          if (cart._id === cartId) {
            // Count which types of reminders were sent
            const emailSent = result.results && result.results.some(r => r.type === 'email' && r.status === 'sent');
            const smsSent = result.results && result.results.some(r => r.type === 'sms' && r.status === 'sent');

            return {
              ...cart,
              status: 'reminded',
              emailReminders: (cart.emailReminders || 0) + (emailSent ? 1 : 0),
              smsReminders: (cart.smsReminders || 0) + (smsSent ? 1 : 0),
              lastReminderDate: new Date().getTime()
            };
          }
          return cart;
        })
      );

      showNotification('success', 'Reminder sent successfully');

      // Refresh stats after sending reminder
      setTimeout(() => {
        fetchData();
      }, 1000);
    } catch (error) {
      console.error('Error sending reminder:', error);
      showNotification('error', 'Failed to send reminder. Please try again.');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Loading state
  if (loading && campaigns.length === 0) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        <img src='../images/loading.gif' alt="Loading..." />
      </div>
    );
  }

  return (
    <div className="marketing-container  space-y-6">

      {deleteModal.show && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setDeleteModal({ show: false, campaignId: null, campaignName: '' })}></div>

            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Campaign</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the campaign "{deleteModal.campaignName}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteCampaign}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setDeleteModal({ show: false, campaignId: null, campaignName: '' })}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Notification */}
      {notification && (
        <div
          className={`p-4 rounded-md mb-4 ${notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
        >
          {notification.message}
          <button
            className="float-right font-bold"
            onClick={() => setNotification(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Dashboard Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800">Marketing Management</h1>
        <div className="flex space-x-3">
          <select
            className="px-7 py-2 border rounded-md text-sm"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </select>
          <button
            onClick={fetchData}
            className="p-2 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            title="Refresh data"
          >
            <RefreshCwIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stats-card bg-white p-4 rounded-lg shadow-sm border border-gray-100">

          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-sm">Abandoned Carts</p>
            <ShoppingCartIcon className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-semibold mt-2">{stats.totalCarts}</p>
          <p className="text-xs text-gray-500 mt-2">In selected time period</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-sm">Emails Sent</p>
            <MailIcon className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-semibold mt-2">{stats.emailsSent}</p>
          <p className="text-xs text-gray-500 mt-2">Cart recovery reminders</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-sm">SMS Sent</p>
            <PhoneIcon className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-semibold mt-2">{stats.smsSent}</p>
          <p className="text-xs text-gray-500 mt-2">Cart recovery reminders</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-sm">Recovered Revenue</p>
            <DollarSignIcon className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-semibold mt-2">${stats.recoveredRevenue || 0}</p>
          <p className="text-xs text-gray-500 mt-2">From recovered carts</p>
        </div>
      </div>

      {/* Campaign Management Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 card-section z-0">
        <div className="flex justify-between items-center mb-6 campaign-controls section-header">
          <h2 className="text-xl font-semibold text-gray-800 ">Cart Recovery Campaigns</h2>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-2"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Create Campaign</span>
          </button>
        </div>

        {campaigns.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No active campaigns. Create a new campaign to start recovering abandoned carts.</p>
          </div>
        ) : (
          <>
            {/* Desktop view */}
            <div className="desktop-table-view">
              <div className="table-responsive">
                <table className="min-w-full divide-y divide-gray-200 campaign-table">
                  <thead className="bg-gray-50 table-header-fixed">
                    <tr>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Freq.</th>
                      <th className="px-2 md:px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                      <th className="px-2 md:px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Max</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="hide-on-tablet px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Run</th>
                      <th className="px-2 md:px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {campaigns.map(campaign => (
                      <tr key={campaign._id} className="compact-row">
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm font-normal text-gray-900">
                          <div className="short-text">{campaign.name || 'Cart Recovery Campaign'}</div>
                        </td>
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm text-gray-500">
                          <div className="flex items-center">
                            <span className="capitalize">{campaign.frequency}</span>
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm text-gray-500 text-center">
                          {campaign.remindersSent || 0}
                        </td>
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm text-gray-500 text-center">
                          {campaign.maxReminders || 6}
                        </td>
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap">
                          <span className={`status-badge inline-flex text-xs leading-5 font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                            campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                            {campaign.status}
                          </span>
                        </td>
                        <td className="hide-on-tablet px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm text-gray-500 date-col">
                          {campaign.nextRun ? formatDate(campaign.nextRun) : 'Not scheduled'}
                        </td>
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm text-gray-500 text-right">
                          <div className="action-buttons flex justify-end space-x-1">
                            {campaign.status === 'paused' ? (
                              <button
                                onClick={() => handleCampaignAction(campaign._id, 'resume')}
                                className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                title="Resume campaign"
                              >
                                <PlayIcon className="w-4 h-4" />
                              </button>
                            ) : campaign.status === 'active' ? (
                              <button
                                onClick={() => handleCampaignAction(campaign._id, 'pause')}
                                className="p-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                                title="Pause campaign"
                              >
                                <PauseIcon className="w-4 h-4" />
                              </button>
                            ) : null}
                            {campaign.status !== 'stopped' && (
                              <button
                                onClick={() => handleCampaignAction(campaign._id, 'stop')}
                                className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                title="Stop campaign"
                              >
                                <StopIcon className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => confirmDeleteCampaign(campaign)}
                              className="p-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                              title="Delete campaign"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>                    </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile card view */}
            <div className="mobile-card-view">
              {campaigns.map(campaign => (
                <div key={campaign._id} className="mobile-card">
                  <div className="mobile-card-header">
                    <h3 className="font-medium text-gray-900 truncate-mobile">{campaign.name || 'Cart Recovery Campaign'}</h3>
                    <span className={`status-badge px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                      campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                      {campaign.status}
                    </span>
                  </div>

                  <div className="mobile-card-content">
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Frequency</span>
                      <span className="mobile-card-value capitalize flex items-center">
                        <CalendarIcon className="w-3 h-3 text-gray-400 mr-1" />
                        {campaign.frequency}
                        {campaign.frequency === 'custom' && campaign.reminderInterval && (
                          <span className="text-xs text-gray-400 ml-1">({campaign.reminderInterval}d)</span>
                        )}
                      </span>
                    </div>

                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Next Run</span>
                      <span className="mobile-card-value flex items-center">
                        <ClockIcon className="w-3 h-3 text-gray-400 mr-1" />
                        <span className="truncate-mobile">{campaign.nextRun ? formatDate(campaign.nextRun) : 'Not scheduled'}</span>
                      </span>
                    </div>

                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Reminders</span>
                      <span className="mobile-card-value">{campaign.remindersSent || 0} / {campaign.maxReminders || 6}</span>
                    </div>
                  </div>

                  <div className="mobile-card-footer">
                    <div className="action-buttons flex space-x-2">
                      {campaign.status === 'paused' ? (
                        <button
                          onClick={() => handleCampaignAction(campaign._id, 'resume')}
                          className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                          title="Resume campaign"
                        >
                          <PlayIcon className="w-4 h-4" />
                        </button>
                      ) : campaign.status === 'active' ? (
                        <button
                          onClick={() => handleCampaignAction(campaign._id, 'pause')}
                          className="p-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                          title="Pause campaign"
                        >
                          <PauseIcon className="w-4 h-4" />
                        </button>
                      ) : null}
                      {campaign.status !== 'stopped' && (
                        <button
                          onClick={() => handleCampaignAction(campaign._id, 'stop')}
                          className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                          title="Stop campaign"
                        >
                          <StopIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => confirmDeleteCampaign(campaign)}
                        className="p-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                        title="Delete campaign"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>


      {/* Abandoned Carts Section */}
      <div className="bg-white p-3 md:p-4 lg:p-6 rounded-lg shadow-sm border border-gray-100 card-section">
        <div className="filter-row flex justify-between items-center mb-4">
          <h2 className="section-header-compact text-md md:text-lg lg:text-xl font-semibold text-gray-800">Abandoned Carts</h2>
          <div className="flex items-center space-x-2">
            <FilterIcon className="w-3 h-3 text-gray-400" />
            <select
              className="filter-control-compact px-2 py-1 border rounded-md text-xs"
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="reminded">Reminded</option>
              <option value="converted">Converted</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        {filteredCarts.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm">
            <p>No abandoned carts found for the selected filter.</p>
          </div>
        ) : (
          <>
            {/* Desktop/Tablet View */}
            <div className="tablet-desktop-view">
              <div className="table-responsive">
                <table className="abandoned-carts-table min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="customer-col text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="items-col text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                      <th className="value-col text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                      <th className="rem-col text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <span className="abbr-text">Rem.</span>
                        <span className="full-text">Reminders</span>
                      </th>
                      <th className="status-col text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="actions-col text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCarts.map(cart => (
                      <tr key={cart._id} className="compact-row">
                        <td className="customer-col py-2 px-2">
                          <div className="customer-details">
                            <div className="customer-email truncate-text text-sm font-medium text-gray-900">{cart.user?.email || 'Guest'}</div>
                            <div className="customer-name truncate-text text-sm text-gray-500">{cart.user?.firstName} {cart.user?.lastName}</div>
                          </div>
                        </td>
                        <td className="items-col text-center text-sm text-gray-500 py-2 px-2">
                          {cart.items?.length || 0}
                        </td>
                        <td className="value-col text-right text-sm text-gray-500 py-2 px-2">
                          ${cart.totalValue?.toFixed(2) || '0.00'}
                        </td>
                        <td className="rem-col text-center py-2 px-2">
                          <div className="reminders-compact flex justify-center space-x-1">
                            {(cart.emailReminders > 0 || cart.smsReminders > 0) ? (
                              <>
                                {cart.emailReminders > 0 && (
                                  <span className="reminder-badge bg-blue-100 text-blue-800 px-1 py-0.5 text-xs rounded-full">{cart.emailReminders}</span>
                                )}
                                {cart.smsReminders > 0 && (
                                  <span className="reminder-badge bg-green-100 text-green-800 px-1 py-0.5 text-xs rounded-full">{cart.smsReminders}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">0</span>
                            )}
                          </div>
                        </td>
                        <td className="status-col py-2 px-2">
                          <span className={`status-badge-compact px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${cart.status === 'converted' ? 'bg-green-100 text-green-800' :
                            cart.status === 'reminded' ? 'bg-blue-100 text-blue-800' :
                              cart.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'
                            }`}>
                            {cart.status}
                          </span>
                        </td>
                        <td className="actions-col text-right py-2 px-2">
                          {(cart.status === 'pending' || cart.status === 'reminded') && (
                            <button
                              onClick={() => openReminderEditor(cart)}
                              className="action-button-compact text-blue-600 hover:text-blue-900 inline-flex items-center space-x-1"
                            >
                              <SendIcon className="w-3 h-3" />
                              <span>Send</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="mobile-card-view">
              {filteredCarts.map(cart => (
                <div key={cart._id} className="mobile-card">
                  <div className="mobile-card-header">
                    <div>
                      <div className="font-medium text-gray-900 truncate-mobile">{cart.user?.email || 'Guest'}</div>
                      <div className="text-sm text-gray-500 truncate-mobile">{cart.user?.firstName} {cart.user?.lastName}</div>
                    </div>
                    <span className={`status-badge px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${cart.status === 'converted' ? 'bg-green-100 text-green-800' :
                      cart.status === 'reminded' ? 'bg-blue-100 text-blue-800' :
                        cart.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                      }`}>
                      {cart.status}
                    </span>
                  </div>

                  <div className="mobile-card-content">
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Items</span>
                      <span className="mobile-card-value">{cart.items?.length || 0}</span>
                    </div>

                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Value</span>
                      <span className="mobile-card-value">${cart.totalValue?.toFixed(2) || '0.00'}</span>
                    </div>

                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Reminders</span>
                      <div className="flex space-x-1">
                        {cart.emailReminders > 0 && (
                          <span className="reminder-badge bg-blue-100 text-blue-800 px-1 py-0.5 text-xs rounded-full">{cart.emailReminders} E</span>
                        )}
                        {cart.smsReminders > 0 && (
                          <span className="reminder-badge bg-green-100 text-green-800 px-1 py-0.5 text-xs rounded-full">{cart.smsReminders} S</span>
                        )}
                        {(cart.emailReminders === 0 && cart.smsReminders === 0) && (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mobile-card-footer">
                    {(cart.status === 'pending' || cart.status === 'reminded') && (
                      <button
                        onClick={() => openReminderEditor(cart)}
                        className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                      >
                        <SendIcon className="w-4 h-4" />
                        <span>Send Reminder</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {/* {(cart.status === 'pending' || cart.status === 'reminded') && (
                      <button
                        onClick={() => sendManualReminder(cart._id)}
                        className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                      >
                        <SendIcon className="w-4 h-4" />
                        <span>Send Reminder</span>
                      </button>
                    )} */}




      {/* Create Campaign Modal */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>

            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Create Cart Recovery Campaign</h3>
                  <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500">
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Campaign Name
                    </label>
                    <input
                      type="text"
                      className={`w-full p-2 border rounded-md ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                      value={campaignData.name}
                      onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frequency
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={campaignData.frequency}
                      onChange={(e) => setCampaignData({ ...campaignData, frequency: e.target.value })}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>

                  {campaignData.frequency === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Days Between Reminders
                      </label>
                      <input
                        type="number"
                        className={`w-full p-2 border rounded-md ${errors.reminderInterval ? 'border-red-500' : 'border-gray-300'}`}
                        min="1"
                        max="90"
                        value={campaignData.reminderInterval}
                        onChange={(e) => setCampaignData({ ...campaignData, reminderInterval: parseInt(e.target.value) || 7 })}
                      />
                      {errors.reminderInterval && <p className="mt-1 text-sm text-red-500">{errors.reminderInterval}</p>}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Reminders
                    </label>
                    <input
                      type="number"
                      className={`w-full p-2 border rounded-md ${errors.maxReminders ? 'border-red-500' : 'border-gray-300'}`}
                      min="1"
                      value={campaignData.maxReminders}
                      onChange={(e) => setCampaignData({ ...campaignData, maxReminders: parseInt(e.target.value) || 6 })}
                    />
                    {errors.maxReminders && <p className="mt-1 text-sm text-red-500">{errors.maxReminders}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Run Date (Optional)
                    </label>
                    <input
                      type="datetime-local"
                      className={`w-full p-2 border rounded-md ${errors.customNextRun ? 'border-red-500' : 'border-gray-300'}`}
                      value={campaignData.customNextRun}
                      onChange={(e) => setCampaignData({ ...campaignData, customNextRun: e.target.value })}
                    />
                    {errors.customNextRun && <p className="mt-1 text-sm text-red-500">{errors.customNextRun}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      Leave blank to start based on frequency (default: tomorrow at 9 AM).
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Channels
                    </label>
                    <div className={`flex space-x-4 ${errors.channels ? 'border border-red-500 p-2 rounded-md' : ''}`}>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          checked={campaignData.channels.includes('email')}
                          onChange={(e) => {
                            const channels = [...campaignData.channels];
                            if (e.target.checked) {
                              if (!channels.includes('email')) channels.push('email');
                            } else {
                              const index = channels.indexOf('email');
                              if (index !== -1) channels.splice(index, 1);
                            }
                            setCampaignData({ ...campaignData, channels });
                          }}
                        />
                        <span className="ml-2">Email</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          checked={campaignData.channels.includes('sms')}
                          onChange={(e) => {
                            const channels = [...campaignData.channels];
                            if (e.target.checked) {
                              if (!channels.includes('sms')) channels.push('sms');
                            } else {
                              const index = channels.indexOf('sms');
                              if (index !== -1) channels.splice(index, 1);
                            }
                            setCampaignData({ ...campaignData, channels });
                          }}
                        />
                        <span className="ml-2">SMS</span>
                      </label>
                    </div>
                    {errors.channels && <p className="mt-1 text-sm text-red-500">{errors.channels}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Template
                    </label>
                    <textarea
                      className="w-full p-2 border border-gray-300 rounded-md h-40 text-sm font-mono"
                      placeholder="Enter custom email template. Use {{firstName}}, {{totalValue}}, and {{itemsList}} as placeholders."
                      value={campaignData.emailTemplate}
                      onChange={(e) => setCampaignData({ ...campaignData, emailTemplate: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave blank to use the default template.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SMS Template
                    </label>
                    <textarea
                      className="w-full p-2 border border-gray-300 rounded-md h-20 text-sm font-mono"
                      placeholder="Enter custom SMS template. Use {{firstName}} and {{totalValue}} as placeholders."
                      value={campaignData.smsTemplate}
                      onChange={(e) => setCampaignData({ ...campaignData, smsTemplate: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave blank to use the default template. Keep under 160 characters.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={createCampaign}
                >
                  Create Campaign
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {reminderModal.show && (
        <div className="fixed z-20 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !reminderModal.loading && setReminderModal({ show: false, cartId: null, cart: null, emailContent: '', smsContent: '', previewMode: true, activeTab: 'email', loading: false })}></div>

            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Send Reminder to {reminderModal.cart?.user?.firstName} {reminderModal.cart?.user?.lastName}
                  </h3>
                  {!reminderModal.loading && (
                    <button onClick={() => setReminderModal({ show: false, cartId: null, cart: null, emailContent: '', smsContent: '', previewMode: true, activeTab: 'email', loading: false })} className="text-gray-400 hover:text-gray-500">
                      <XIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="-mb-px flex space-x-4" aria-label="Tabs">
                    <button
                      onClick={() => setReminderModal(prev => ({ ...prev, activeTab: 'email' }))}
                      className={`${reminderModal.activeTab === 'email' || reminderModal.activeTab === 'both'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                      disabled={reminderModal.loading}
                    >
                      <MailIcon className={`mr-2 h-5 w-5 ${reminderModal.activeTab === 'email' || reminderModal.activeTab === 'both' ? 'text-blue-500' : 'text-gray-400'}`} />
                      Email
                      <input
                        type="checkbox"
                        checked={reminderModal.activeTab === 'email' || reminderModal.activeTab === 'both'}
                        onChange={() => {
                          if (reminderModal.activeTab === 'email') {
                            setReminderModal(prev => ({ ...prev, activeTab: 'none' }));
                          } else if (reminderModal.activeTab === 'both') {
                            setReminderModal(prev => ({ ...prev, activeTab: 'sms' }));
                          } else if (reminderModal.activeTab === 'sms') {
                            setReminderModal(prev => ({ ...prev, activeTab: 'both' }));
                          } else {
                            setReminderModal(prev => ({ ...prev, activeTab: 'email' }));
                          }
                        }}
                        className="ml-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        disabled={reminderModal.loading}
                      />
                    </button>

                    <button
                      onClick={() => setReminderModal(prev => ({ ...prev, activeTab: 'sms' }))}
                      className={`${reminderModal.activeTab === 'sms' || reminderModal.activeTab === 'both'
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                      disabled={reminderModal.loading}
                    >
                      <PhoneIcon className={`mr-2 h-5 w-5 ${reminderModal.activeTab === 'sms' || reminderModal.activeTab === 'both' ? 'text-green-500' : 'text-gray-400'}`} />
                      SMS
                      <input
                        type="checkbox"
                        checked={reminderModal.activeTab === 'sms' || reminderModal.activeTab === 'both'}
                        onChange={() => {
                          if (reminderModal.activeTab === 'sms') {
                            setReminderModal(prev => ({ ...prev, activeTab: 'none' }));
                          } else if (reminderModal.activeTab === 'both') {
                            setReminderModal(prev => ({ ...prev, activeTab: 'email' }));
                          } else if (reminderModal.activeTab === 'email') {
                            setReminderModal(prev => ({ ...prev, activeTab: 'both' }));
                          } else {
                            setReminderModal(prev => ({ ...prev, activeTab: 'sms' }));
                          }
                        }}
                        className="ml-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        disabled={reminderModal.loading}
                      />
                    </button>

                    <div className="flex-grow"></div>

                    <button
                      onClick={() => setReminderModal(prev => ({ ...prev, previewMode: !prev.previewMode }))}
                      className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
                      disabled={reminderModal.loading}
                    >
                      {reminderModal.previewMode ? 'Edit HTML' : 'Preview'}
                    </button>
                  </nav>
                </div>

                {/* Content based on active tab */}
                <div className="space-y-4">
                  {/* Email Content */}
                  {(reminderModal.activeTab === 'email' || reminderModal.activeTab === 'both') && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Email Content</h4>

                      {reminderModal.previewMode ? (
                        <div className="border rounded-md p-4 bg-white max-h-80 overflow-auto">
                          <div
                            className="email-preview"
                            dangerouslySetInnerHTML={{ __html: reminderModal.emailContent }}
                            style={{
                              fontFamily: 'Arial, sans-serif',
                              lineHeight: '1.6',
                              color: '#333'
                            }}
                          />
                        </div>
                      ) : (
                        <textarea
                          className="w-full p-2 border border-gray-300 rounded-md font-mono text-sm h-80"
                          value={reminderModal.emailContent}
                          onChange={(e) => setReminderModal(prev => ({ ...prev, emailContent: e.target.value }))}
                          disabled={reminderModal.loading}
                        />
                      )}
                    </div>
                  )}

                  {/* SMS Content */}
                  {(reminderModal.activeTab === 'sms' || reminderModal.activeTab === 'both') && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">SMS Content ({160 - reminderModal.smsContent.length} characters left)</h4>

                      {reminderModal.previewMode ? (
                        <div className="border rounded-md p-4 bg-gray-100 max-h-32">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-w-xs mx-auto">
                            <p className="text-gray-800 text-sm">
                              {reminderModal.smsContent}
                            </p>
                            <p className="text-right text-xs text-gray-500 mt-1">
                              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <textarea
                          className="w-full p-2 border border-gray-300 rounded-md font-mono text-sm h-32"
                          value={reminderModal.smsContent}
                          onChange={(e) => setReminderModal(prev => ({ ...prev, smsContent: e.target.value }))}
                          maxLength={160}
                          disabled={reminderModal.loading}
                        />
                      )}
                      <p className={`text-xs mt-1 ${reminderModal.smsContent.length > 160 ? 'text-red-500' : 'text-gray-500'}`}>
                        SMS messages should be kept under 160 characters.
                      </p>
                    </div>
                  )}

                  {reminderModal.activeTab === 'none' && (
                    <div className="text-center py-8 text-gray-500">
                      <p>Please select at least one channel (Email or SMS) to send the reminder.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${reminderModal.activeTab === 'none'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    } sm:ml-3 sm:w-auto sm:text-sm`}
                  onClick={sendCustomReminder}
                  disabled={reminderModal.activeTab === 'none' || reminderModal.loading}
                >
                  {reminderModal.loading ? 'Sending...' : 'Send Reminder'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => !reminderModal.loading && setReminderModal({ show: false, cartId: null, cart: null, emailContent: '', smsContent: '', previewMode: true, activeTab: 'email', loading: false })}
                  disabled={reminderModal.loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      <style >{`
     /* Base responsive container */
      .marketing-container {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
        padding: 0.5rem;
      }

      /* Card sections */
      .card-section {
        margin-bottom: 1.5rem;
      }

      /* Table container with horizontal scroll on small screens */
      .table-responsive {
        width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }

      /* Make sure tables don't expand beyond their container */
      table {
        width: 100%;
        border-collapse: collapse;
      }

      /* Ensure status badges don't wrap */
      .status-badge, .status-badge-compact {
        white-space: nowrap;
        display: inline-flex;
        align-items: center;
        border-radius: 9999px;
        font-weight: 600;
        font-size: 0.75rem;
        line-height: 1.25rem;
      }

      /* Handle long text content */
      .truncate-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      /* Section headers */
      .section-header, .section-header-compact {
        position: sticky;
        top: 0;
        background-color: white;
        z-index: 10;
      }

      /* Force rows to be more compact */
      .compact-row td {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
      }

      /* Fix table header alignment issues */
      .table-header-fixed {
        position: sticky;
        top: 0;
        background-color: #f9fafb;
        z-index: 5;
      }

      /* Email preview styling */
      .email-preview h2 {
        font-size: 20px;
        margin-bottom: 16px;
        color: #333;
      }

      .email-preview p {
        margin-bottom: 16px;
      }

      .email-preview .highlight {
        background-color: #f8f9fa;
        border-left: 4px solid #4a6ee0;
        padding: 15px;
        margin: 20px 0;
      }

      .email-preview ul {
        list-style-type: disc;
        padding-left: 20px;
        margin-bottom: 16px;
      }

      .email-preview strong {
        font-weight: 600;
      }

      /* Show/hide text based on screen size */
      .abbr-text {
        display: none;
      }

      .full-text {
        display: inline;
      }

      /* Abandoned Carts Table Column Optimization */
      .abandoned-carts-table {
        table-layout: fixed;
      }

      .customer-col {
        width: 30%;
      }

      .items-col, .value-col, .rem-col {
        width: 10%;
      }

      .status-col {
        width: 15%;
      }

      .actions-col {
        width: 15%;
      }

      /* Mobile Card View */
      .mobile-card-view {
        display: none;
      }

      .filter-row {
        margin-bottom: 1rem;
      }

      /* Table/Desktop View - Hide by default on mobile */
      @media (max-width: 767px) {
     .desktop-table-view {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        max-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      
      .tablet-desktop-view {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        max-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
        
        .mobile-card-view {
          display: block !important;
        }
        
        .mobile-card-view {
          display: block;
        }
        
        .mobile-card {
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-bottom: 1rem;
          background-color: white;
        }
        
        .mobile-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .mobile-card-content {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }
        
        .mobile-card-item {
          display: flex;
          flex-direction: column;
        }
        
        .mobile-card-label {
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }
        
        .mobile-card-value {
          font-size: 0.875rem;
        }
        
        .mobile-card-footer {
          display: flex;
          justify-content: flex-end;
          padding-top: 0.5rem;
          border-top: 1px solid #f0f0f0;
        }
        
        /* Limit text width on mobile */
        .truncate-mobile {
          max-width: 150px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      @media (min-width: 768px) {
        .mobile-card-view {
          display: none !important;
        }
        
        .desktop-table-view, 
        .tablet-desktop-view {
          display: block !important;
        }
      }

      /* Media query specifically for tablets */
      @media (min-width: 768px) and (max-width: 1200px) {
        /* Show abbreviated text */
        .abbr-text {
          display: inline;
        }
        
        .full-text {
          display: none;
        }
        
        /* Overall container padding */
        .marketing-container {
          padding: 0.5rem;
        }
        
        /* Remove unnecessary padding in cards */
        .card-section {
          padding: 0.75rem;
        }
        
        /* Make tables more compact for tablets */
        .table-responsive {
          font-size: 0.75rem;
        }
        
        .table-responsive th,
        .table-responsive td {
          padding: 0.5rem 0.25rem;
        }
        
        /* Hide less important columns on tablet */
        .hide-on-tablet {
          display: none;
        }
        
        /* Shorten the display text for certain cells */
        .short-text {
          max-width: 80px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        /* Make status badges smaller */
        .status-badge, .status-badge-compact {
          padding: 0.1rem 0.5rem;
          font-size: 0.65rem;
        }
        
        /* Filter controls adjustments */
        .filter-control-compact {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }
        
        /* Optimize reminders display */
        .reminders-compact {
          white-space: nowrap;
        }
        
        /* Optimize table layout for tablet */
        .abandoned-carts-table {
          table-layout: fixed;
        }
        
        .customer-col {
          width: 25%;
        }
        
        .items-col, .value-col, .rem-col {
          width: 12%;
        }
        
        .status-col {
          width: 18%;
        }
        
        .actions-col {
          width: 15%;
        }
        
        /* Make section headers more compact */
        .section-header-compact {
          font-size: 1rem;
        }
        
        /* Better touch targets */
        button, select {
          min-height: 1.75rem;
        }
      }

      /* Large screens */
      @media (min-width: 1201px) {
        /* Campaign Table Columns */
        .campaign-table {
          table-layout: fixed;
        }
        
        .campaign-table th:nth-child(1),
        .campaign-table td:nth-child(1) {
          width: 20%; /* Campaign name */
        }
        
        .campaign-table th:nth-child(2),
        .campaign-table td:nth-child(2) {
          width: 10%; /* Frequency */
        }
        
        .campaign-table th:nth-child(3),
        .campaign-table td:nth-child(3),
        .campaign-table th:nth-child(4),
        .campaign-table td:nth-child(4) {
          width: 8%; /* Sent and Max */
          text-align: center;
        }
        
        .campaign-table th:nth-child(5),
        .campaign-table td:nth-child(5) {
          width: 12%; /* Status */
        }
        
        .campaign-table th:nth-child(6),
        .campaign-table td:nth-child(6) {
          width: 25%; /* Next Run */
        }
        
        .campaign-table th:nth-child(7),
        .campaign-table td:nth-child(7) {
          width: 15%; /* Actions */
          text-align: right;
        }
        
        /* Abandoned Carts Table Columns */
        .abandoned-carts-table {
          table-layout: fixed;
        }
        
        .abandoned-carts-table .customer-col {
          width: 28%;
        }
        
        .abandoned-carts-table .items-col {
          width: 10%;
        }
        
        .abandoned-carts-table .value-col {
          width: 15%;
        }
        
        .abandoned-carts-table .rem-col {
          width: 15%;
        }
        
        .abandoned-carts-table .status-col {
          width: 15%;
        }
        
        .abandoned-carts-table .actions-col {
          width: 12%;
        }
      
        .full-text {
          display: inline;
        }
        
        .abbr-text {
          display: none;
        }
      }
        /* Enhanced table styling */
      .campaign-table th,
      .abandoned-carts-table th {
        padding: 0.75rem 0.5rem;
        font-weight: 600;
        text-transform: uppercase;
        font-size: 0.75rem;
        color: #4b5563;
        border-bottom: 2px solid #e5e7eb;
      }

      .campaign-table td,
      .abandoned-carts-table td {
        padding: 0.75rem 0.5rem;
        vertical-align: middle;
      }

      /* Improved hover effect for rows */
      .campaign-table tr:hover,
      .abandoned-carts-table tr:hover {
        background-color: #f9fafb;
      }

      /* Make the status badges more consistent */
      .status-badge, .status-badge-compact {
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      /* Fix text alignment in table cells */
      .text-center {
        text-align: center !important;
      }

      .text-right {
        text-align: right !important;
      }

      .text-left {
        text-align: left !important;
      }
      `}</style>


    </div>
  );
};

export default MarketingManagement;